import { ChangeDetectionStrategy, Component, inject, computed, ElementRef, HostListener, signal } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '../../../auth/services/auth.service';
import {
    selectCountUnreadableNotifications,
} from '../../../notification-center/store/selectors/profileNotifications.selector';
import { SUPPORT_URL } from '../../constants';
import { NotificationsService } from '../../services/notifications.service';
import { openCreateTournamentSelectGameModalAction } from '../../store/actions/commonSettings.action';

import { MobileSideMenuComponent } from '../mobile-side-menu/mobile-side-menu.component';

import { TranslatePipe } from '@ngx-translate/core';
import { EditUserProfileComponent } from '../edit-user-profile/edit-user-profile.component';
import { PERMISSION_NAMES } from 'src/app/permissions/models/enums/permission-names';
import { selectGeneralPermissions } from 'src/app/permissions/store/selectors/permissions.selector';
import { SvgIconComponent } from 'angular-svg-icon';

export interface HeaderNavigation {
    key?: string;
    title: string;
    icon: string;
    url: string;
    useExact?: boolean;
    mobileOnly?: boolean;
    newTab?: boolean;
    badge?: number | string | null;
}

@Component({

    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MobileSideMenuComponent,
        RouterLink,
        RouterLinkActive,
        TranslatePipe,
        EditUserProfileComponent,
        SvgIconComponent
        ],
})
export class HeaderComponent {
    private store = inject(Store);
    private router = inject(Router);
    private authService = inject(AuthService);
    private notificationsService = inject(NotificationsService);

    userPermissions = this.store.selectSignal(selectGeneralPermissions);
    isAuthenticatedSig = toSignal(this.authService.isAuthenticated$, { initialValue: this.authService.isAuthorised });

    isAuthorised = computed(() => this.isAuthenticatedSig());
    isPlayer = computed(() => this.isAuthenticatedSig() && this.authService.isPlayer);

    canBuyEarlyAccess = computed(() =>
        this.userPermissions()?.includes(PERMISSION_NAMES.SUBSCRIPTIONS_PURCHASE_EARLY_PASS) ?? false
    );

    canCreateLidomaTournament = computed(() =>
        this.userPermissions()?.includes(PERMISSION_NAMES.TOURNAMENT_CREATE_NEW) ?? false
    );

    canCreateTournament = computed(() =>
        this.userPermissions()?.includes(PERMISSION_NAMES.TOURNAMENT_CREATE_NEW_CUSTOM) ?? false
    );

    hasEarlySubscription = computed(() => {
        const permissions = this.userPermissions() || [];
        const hasPurchaseEarlyPass = permissions.includes(PERMISSION_NAMES.SUBSCRIPTIONS_PURCHASE_EARLY_PASS);
        const hasJoinSolo = permissions.includes(PERMISSION_NAMES.TOURNAMENT_JOIN_EARLY_ACCESS_SOLO);
        const hasJoinTeam = permissions.includes(PERMISSION_NAMES.TOURNAMENT_JOIN_EARLY_ACCESS_TEAM);
        const isOnTournamentPage = window.location.href.includes('all-tournaments') || window.location.href.includes('tournaments');

        return hasPurchaseEarlyPass && (hasJoinSolo || hasJoinTeam || isOnTournamentPage);
    });

    isScrolled = signal(false);

    navigation: HeaderNavigation[] = [
        {
            title: 'home',
            icon: 'assets/img/icon/navigation/home.svg',
            url: '/',
            useExact: true,
        },
        {
            title: 'tournaments',
            icon: 'assets/img/icon/navigation/tournaments.svg',
            url: '/all-tournaments',
            useExact: false,
        },
        {
            title: 'leaderboard',
            icon: 'assets/img/icon/navigation/leaderboard.svg',
            url: '/leaderboard',
            useExact: true,
        },
        {
            title: 'games',
            icon: 'assets/img/icon/navigation/games.svg',
            url: '/games',
            useExact: true,
        },
        {
            title: 'BGMI',
            icon: 'assets/img/icon/navigation/games.svg',
            url: '/games/BGMI',
            useExact: true,

        },{
            title: 'Rewards',
            icon: 'assets/img/icon/navigation/rewards.svg',
            url: '/rewards',
            useExact: true,
        },
        {
            title: 'support',
            icon: 'assets/img/icon/support.png',
            url: SUPPORT_URL,
            newTab: true,
            mobileOnly: true,
        },
    ];

    countUnreadableUserNotificationsSig = this.store.selectSignal(
        selectCountUnreadableNotifications
    );

    logIn() {
        this.authService.login();
    }

    openCreateTournamentModal() {
        if (!this.isAuthorised()) {
            this.notificationsService.showShouldLoginMessage();
            return;
        }

        this.store.dispatch(openCreateTournamentSelectGameModalAction());
    }

    openNotifications() {
        this.router.navigateByUrl('/notification-center');
    }

    @HostListener('window:scroll', [])
    onScroll() {
        this.isScrolled.set(window.scrollY > 50);
    }
}
